import { AnimatePresence, motion } from 'motion/react'
import { useTranslations } from 'next-intl'
import { useEffect, useState } from 'react'
import { useMeasure } from 'react-use'
import { Button } from '@/components/button'
import { SvgOutlinedChevronRight } from '@/icons/svg-outlined-chevron-right'
import { ILinkParams, IUrlPattern } from '@/setup/routing'
import { markdownToHtmlAsync } from '@/utils/markdown/faq-parser'
import { FaqContentSegment } from '@/utils/markdown/remark-utils'
import { COLORS } from '@/utils/styles/colors'

export interface IFaqItemProps {
    question: string
    answer: string
    cta?: {
        label: string
        href: ILinkParams<IUrlPattern> | { externalUrl: string }
    }
    segments?: FaqContentSegment[]
}

export const FaqItem = ({ question, answer, cta, segments }: IFaqItemProps) => {
    const t = useTranslations('faq.messages')
    const [isOpen, setIsOpen] = useState(false)
    const [renderedSegments, setRenderedSegments] = useState<{ [key: number]: string }>({})
    const [isProcessing, setIsProcessing] = useState(false)

    const [innerRef, { height: innerHeight }] = useMeasure<HTMLDivElement>()

    useEffect(() => {
        if (!isOpen || !segments || segments.length === 0) return

        const processSegments = async () => {
            setIsProcessing(true)
            const newRenderedSegments: { [key: number]: string } = {}

            try {
                for (let i = 0; i < segments.length; i++) {
                    const segment = segments[i]
                    if (segment.type === 'text' && segment.content) {
                        try {
                            const html = await markdownToHtmlAsync(segment.content)
                            newRenderedSegments[i] = html
                        } catch (error) {
                            // biome-ignore lint/suspicious/noConsole: intentional error logging
                            console.error('Error rendering segment:', error)
                            newRenderedSegments[i] = segment.content.replace(/\n/g, '<br>')
                        }
                    }
                }

                setRenderedSegments(newRenderedSegments)
            } catch (error) {
                // biome-ignore lint/suspicious/noConsole: intentional error logging
                console.error('Error processing segments:', error)
            } finally {
                setIsProcessing(false)
            }
        }

        processSegments()
    }, [isOpen, segments])

    const buttonToCta = (button: FaqContentSegment['button']) => {
        if (!button) return undefined

        return {
            label: button.label,
            href:
                button.type === 'internal'
                    ? ({
                          pattern: button.href as IUrlPattern,
                          params: undefined,
                      } as unknown as ILinkParams<IUrlPattern>)
                    : { externalUrl: button.href },
        }
    }

    const renderContent = () => {
        if (segments && segments.length > 0) {
            return (
                <div className="flex flex-col gap-4">
                    {isProcessing && <div className="text-black/70 text-sm">{t('processing')}</div>}
                    {segments.map((segment, index) => {
                        if (segment.type === 'text' && segment.content) {
                            const htmlContent = renderedSegments[index]
                            return (
                                <div
                                    key={index}
                                    className="text-black/70 text-[14px] prose prose-sm max-w-none prose-a:underline prose-a:text-black [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5 [&_li]:my-1 [&_ul_ul]:list-[circle] [&_ul_ul]:pl-5 [&_ol_ol]:pl-5"
                                    dangerouslySetInnerHTML={{
                                        __html:
                                            htmlContent || segment.content.replace(/\n/g, '<br>'),
                                    }}
                                />
                            )
                        } else if (segment.type === 'button' && segment.button) {
                            const buttonCta = buttonToCta(segment.button)
                            if (!buttonCta) return null

                            return (
                                <Button
                                    key={index}
                                    shape="pill"
                                    width="max-content"
                                    height="46px"
                                    styleType="primary"
                                    href={buttonCta.href}
                                    target="_blank"
                                >
                                    {segment.button.label}
                                </Button>
                            )
                        }
                        return null
                    })}
                </div>
            )
        }

        return (
            <>
                <p className="text-black/70 text-[14px]">{answer}</p>
                {cta && (
                    <Button
                        shape="pill"
                        width="max-content"
                        height="46px"
                        styleType="primary"
                        href={cta.href}
                        target="_blank"
                    >
                        {cta.label}
                    </Button>
                )}
            </>
        )
    }

    return (
        <AnimatePresence initial={false}>
            <motion.div
                initial={{
                    height: innerHeight || '100%',
                }}
                animate={{
                    height: innerHeight || '100%',
                }}
                transition={{
                    height: { type: 'spring', duration: 0.4, bounce: 0.05 },
                }}
                className="overflow-hidden rounded-2xl w-full px-6 bg-[#F5F5F5] text-start"
            >
                <div ref={innerRef}>
                    <div
                        onClick={() => setIsOpen((prev) => !prev)}
                        className="flex justify-between items-center cursor-pointer py-6 select-none gap-2"
                    >
                        <h3 className="font-bold text-[16px] md:text-[18px]">{question}</h3>

                        <motion.div
                            initial={{ rotate: isOpen ? 270 : 90 }}
                            animate={{ rotate: isOpen ? 270 : 90 }}
                        >
                            <SvgOutlinedChevronRight
                                color={COLORS.black}
                                size={24}
                                style={{ position: 'relative', top: '-1px' }}
                            />
                        </motion.div>
                    </div>

                    <AnimatePresence initial={false} mode="popLayout">
                        {isOpen && (
                            <motion.div
                                initial={{ opacity: 1 }}
                                animate={{ opacity: 1, transition: { duration: 0 } }}
                                exit={{ opacity: 0, transition: { duration: 0.2 } }}
                                className="flex flex-col gap-6 pb-8 "
                            >
                                {renderContent()}
                            </motion.div>
                        )}
                    </AnimatePresence>
                </div>
            </motion.div>
        </AnimatePresence>
    )
}
